/*
 * @file Fix Mobile Viewports
 *
 * Mobile browsers (especially iOS Safari) have inconsistent viewport height behavior:
 * - Address bar, toolbar etc can show/hide dynamically
 * - Virtual keyboard can appear/disappear
 * - The CSS `100vh` unit doesn't always reflect these changes properly
 *
 * This causes layout issues where content gets cut off or doesn't fill the screen correctly.
 *
 * Solution: We set CSS custom properties (--vh100, --top0) that can be used instead of
 * `100vh` and `top: 0` to get accurate, dynamically-updated viewport dimensions.
 */

import device from '@@/bits/device'
import { isActiveElementAnInputField } from '@@/bits/dom'
import { observeVisualViewportSize } from '@@/bits/window'

let alreadyFixing = false

const fixViewportHeight = (height: number, scrollY: number): void => {
  if (typeof document.documentElement.style.setProperty === 'function') {
    document.documentElement.style.setProperty('--vh100', `${height}px`)
    document.documentElement.style.setProperty('--top0', `${scrollY}px`)
  }
}

const fixMobileViewport = (): void => {
  if (device.mobile && !device.app && !alreadyFixing) {
    observeVisualViewportSize((dimensions) => {
      // Android WebViews shrink the layout for the keyboard, so --vh100 must follow it
      if (isActiveElementAnInputField() && !device.androidWebView) return

      // PRO-21681 Skip during pinch-to-zoom to prevent layout issues.
      const isZoomedIn = dimensions.scale > 1
      if (!isZoomedIn) {
        const currentScrollY = window.scrollY
        fixViewportHeight(dimensions.height, currentScrollY)
      }
    })
    alreadyFixing = true

    const initialHeight = window.visualViewport != null ? window.visualViewport.height : window.innerHeight
    const initialScrollY = window.scrollY
    fixViewportHeight(initialHeight, initialScrollY)
  }
}

export default fixMobileViewport
